import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Alert, Button, Card, DatePicker, Empty, Form, InputNumber, Modal, Select, Spin, Tag, Tooltip, Typography, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { PlusCircle, Trash2 } from "react-bootstrap-icons";
import DropdownActions from "../../../../components/DropdownActions";
import appConfigStore from "../../../../stores/appConfigStore";
import AdminClientFeaturesStore, { featureErrorMessage, IAdminCompanyFeature } from "../../../../stores/admin/adminClientFeaturesStore";
import styles from "./index.module.scss";

interface FeatureForm {
  featureCode: string;
  quantity: number;
  expiresAt: Dayjs;
}

function quantityLabel(code?: string) {
  if (code === "additional_storage") return "Объём, ГБ";
  if (code === "additional_employees") return "Дополнительные сотрудники";
  return "Количество";
}

const AdminClientFeatures = observer(({ ownerId }: { ownerId: string }) => {
  if (!appConfigStore.isSuperAdmin) return null;
  return <ClientFeatures ownerId={ownerId} />;
});

const ClientFeatures = observer(({ ownerId }: { ownerId: string }) => {
  const store = useMemo(() => new AdminClientFeaturesStore(ownerId), [ownerId]);
  const [form] = Form.useForm<FeatureForm>();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<IAdminCompanyFeature>();
  const [formError, setFormError] = useState<string>();
  const selectedCode = Form.useWatch("featureCode", form);
  const selectedFeature = store.availableFeatures.find(feature => feature.code === selectedCode);

  useEffect(() => {
    store.load();
    setIsOpen(false);
  }, [store]);

  const openForm = (record?: IAdminCompanyFeature) => {
    setEditing(record);
    setFormError(undefined);
    form.resetFields();
    form.setFieldsValue(record ? {
      featureCode: record.feature?.code,
      quantity: record.quantity ?? 1,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : undefined,
    } : { quantity: 1, expiresAt: dayjs().add(1, "month").endOf("day") });
    setIsOpen(true);
  };

  const save = async () => {
    let values: FeatureForm;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setFormError(undefined);
    try {
      await store.save({
        ...(!editing ? { featureCode: values.featureCode } : {}),
        quantity: selectedFeature?.type === "boolean" ? 1 : values.quantity,
        expiresAt: values.expiresAt.toISOString(),
      }, editing?.id);
      setIsOpen(false);
      message.success(editing ? "Доп. функционал обновлён" : "Доп. функционал выдан");
    } catch (error) {
      setFormError(featureErrorMessage(error));
    }
  };

  const revoke = (record: IAdminCompanyFeature) => {
    Modal.confirm({
      title: "Удалить выдачу доп. функционала?",
      content: `Выдача «${record.feature?.title ?? "Доп. функционал"}» завершится сразу. Другие выдачи этой фичи продолжат действовать.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      async onOk() {
        try {
          await store.revoke(record.id);
          message.success("Выдача завершена");
        } catch (error) {
          message.error(featureErrorMessage(error));
          throw error;
        }
      },
    });
  };

  return (
    <div className={styles.column}>
      <div className={styles.header}>
        <div className={styles.title}>Доп. функционал</div>
        <Button type="text" size="small" aria-label="Выдать доп. функционал" title="Выдать доп. функционал"
          icon={<PlusCircle size={18} color="var(--primary)" />}
          disabled={store.isLoading || !!store.error || store.isSaving || !store.availableFeatures.length} onClick={() => openForm()} />
      </div>
      <div className={styles.features}>
        <Typography.Text type="secondary">Действует для всей компании клиента.</Typography.Text>
        {store.isLoading ? <Spin /> : store.error ? (
          <Alert type="error" showIcon message={store.error} action={<Button onClick={() => store.load()}>Повторить</Button>} />
        ) : store.data.length === 0 ? <Empty description="Доп. функционал ещё не выдавался" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          store.data.map(record => {
            const active = !record.expiresAt || dayjs(record.expiresAt).isAfter(dayjs());
            const renewable = record.subscription?.status === "active" || record.subscription?.status === "failed";
            return (
              <Card key={record.id} size="small" className={styles.featureCard}>
                <div className={styles.featureActions}>
                  <DropdownActions
                    onEdit={() => openForm(record)} editLabel="Изменить" editDisabled={store.isSaving || !record.feature || renewable}
                    actions={active && !renewable ? [{ icon: <Trash2 />, label: "Удалить", disabled: store.isSaving, onClick: () => revoke(record) }] : []}
                  />
                </div>
                <div className={styles.featureDetails}>
                  <h3 className={styles.featureTitle}>{record.feature?.title ?? "Неизвестная фича"}</h3>
                  <div className={styles.featureStatuses}>
                    <Tag className={styles.featureStatus} color={active ? "green" : "default"}>{active ? "Действует" : "Завершена"}</Tag>
                    {renewable && (
                      <Tooltip trigger={["hover", "focus"]} title="Ручное изменение недоступно. Сначала отключите автопродление в настройках компании или создайте отдельную ручную выдачу.">
                        <Tag className={`${styles.featureStatus} ${styles.renewalStatus}`} color="blue" tabIndex={0}>Автопродление включено</Tag>
                      </Tooltip>
                    )}
                  </div>
                  {record.feature?.type === "limit" && <div>{quantityLabel(record.feature.code)}: {record.quantity}</div>}
                  <div>Выдано: {dayjs(record.createdAt).format("DD.MM.YYYY HH:mm")}</div>
                  <div>Окончание: {record.expiresAt ? dayjs(record.expiresAt).format("DD.MM.YYYY HH:mm") : "Без ограничения"}</div>
                </div>
              </Card>
            );
          })
        )}
      </div>
      <Modal title={editing ? "Изменить доп. функционал" : "Выдать доп. функционал"} open={isOpen}
        onOk={save} onCancel={() => { if (!store.isSaving) setIsOpen(false); }}
        confirmLoading={store.isSaving} cancelButtonProps={{ disabled: store.isSaving }}
        okText={editing ? "Сохранить" : "Выдать"} cancelText="Отмена">
        <Form form={form} layout="vertical" disabled={store.isSaving}>
          {formError && <Alert type="error" showIcon message={formError} style={{ marginBottom: 16 }} />}
          <Form.Item name="featureCode" label="Функционал" rules={[{ required: true, message: "Выберите фичу" }]}>
            <Select disabled={!!editing} placeholder="Выберите фичу" options={store.availableFeatures.map(feature => ({ value: feature.code, label: feature.title }))}
              onChange={() => form.setFieldValue("quantity", 1)} />
          </Form.Item>
          {selectedFeature?.type === "limit" && (
            <Form.Item name="quantity" label={quantityLabel(selectedCode)} rules={[
              { required: true, message: "Укажите количество" },
              { type: "integer", min: 1, max: 2147483647, message: "Укажите целое положительное количество" },
            ]}>
              <InputNumber disabled={!!editing?.subscription} min={1} max={2147483647} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          )}
          {editing?.subscription && selectedFeature?.type === "limit" && (
            <Typography.Paragraph type="secondary">Количество связано с платёжной подпиской и не изменяется вручную. Для дополнительных лимитов создайте отдельную выдачу.</Typography.Paragraph>
          )}
          <Form.Item name="expiresAt" label="Действует до" style={{ marginBottom: 8 }} rules={[
            { required: true, message: "Укажите срок окончания" },
            { validator: (_, value?: Dayjs) => !value || value.isAfter(dayjs()) ? Promise.resolve() : Promise.reject(new Error("Дата окончания должна быть в будущем")) },
          ]}>
            <DatePicker showTime={{ format: "HH:mm" }} format="DD.MM.YYYY HH:mm" style={{ width: "100%" }}
              disabledDate={date => date.isBefore(dayjs(), "day")} />
          </Form.Item>
          <Typography.Text type="secondary">Доступ действует сразу. После указанного срока выдача автоматически перестанет действовать.</Typography.Text>
        </Form>
      </Modal>
    </div>
  );
});

export default AdminClientFeatures;
